import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react'

type Price = { bid: number; ask: number }

const prices = new Map<string, Price>()

const listeners = new Map<string, Set<() => void>>()

type PriceTarget = {
  symbol?: string | null
  side?: string | null
}

function getListeners(symbol: string) {
  let set = listeners.get(symbol)
  if (!set) {
    set = new Set<() => void>()
    listeners.set(symbol, set)
  }
  return set
}

export function updateCurrentPrices(batch: Record<string, Price>) {
  for (const [symbol, value] of Object.entries(batch)) {
    const prev = prices.get(symbol)
    if (!prev || prev.bid !== value.bid || prev.ask !== value.ask) {
      prices.set(symbol, value)
      getListeners(symbol).forEach((l) => l())
    }
  }
}

export function initializeCurrentPrices(batch: Record<string, Price>) {
  for (const [symbol, value] of Object.entries(batch)) {
    if (!prices.has(symbol)) {
      prices.set(symbol, value)
      getListeners(symbol).forEach((l) => l())
    }
  }
}

function selectPriceValue(
  symbol?: string | null,
  side?: string | null,
  priceType: 'position' | 'order' = 'position'
) {
  if (!symbol) return undefined
  if (side !== 'BUY' && side !== 'SELL') return undefined
  const p = prices.get(symbol)
  if (!p) return undefined
  const isBuy = side === 'BUY'
  if (priceType === 'position') {
    return isBuy ? p.bid : p.ask
  }
  return isBuy ? p.ask : p.bid
}

export function useCurrentPriceForTrade(
  symbol?: string | null,
  side?: string | null,
  priceType: 'position' | 'order' = 'position'
) {
  const subscribe = useMemo(
    () => (listener: () => void) => {
      if (!symbol) return () => undefined
      const set = getListeners(symbol)
      set.add(listener)
      return () => set.delete(listener)
    },
    [symbol]
  )

  const getSnapshot = useCallback(
    () => selectPriceValue(symbol, side, priceType),
    [symbol, side, priceType]
  )

  const getServerSnapshot = useCallback(() => undefined, [])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

function serializeSymbolSet(targets: PriceTarget[]): string {
  if (!targets.length) return ''
  const unique = new Set<string>()
  targets.forEach((target) => {
    if (target.symbol) unique.add(target.symbol)
  })
  return Array.from(unique).sort().join('|')
}

export function useCurrentPricesForTrades(
  trades: PriceTarget[],
  priceType: 'position' | 'order' = 'position'
): Array<number | undefined> {
  const snapshotRef = useRef<Array<number | undefined>>([])
  const symbolsSignature = useMemo(() => serializeSymbolSet(trades), [trades])
  const uniqueSymbols = useMemo(
    () => (symbolsSignature ? symbolsSignature.split('|') : []),
    [symbolsSignature]
  )

  const subscribe = useMemo(
    () => (listener: () => void) => {
      if (uniqueSymbols.length === 0) return () => undefined
      const unsubscribers = uniqueSymbols.map((symbol) => {
        const set = getListeners(symbol)
        set.add(listener)
        return () => set.delete(listener)
      })
      return () => {
        unsubscribers.forEach((unsubscribe) => unsubscribe())
      }
    },
    [uniqueSymbols]
  )

  const getSnapshot = useCallback(() => {
    const next = trades.map((trade) => selectPriceValue(trade.symbol, trade.side, priceType))
    const prev = snapshotRef.current
    const isSameLength = prev.length === next.length
    const isSameContent =
      isSameLength && prev.every((value, index) => Object.is(value, next[index]))

    if (isSameContent) {
      return prev
    }

    snapshotRef.current = next
    return next
  }, [trades, priceType])

  const getServerSnapshot = useCallback(() => trades.map(() => undefined), [trades])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
